import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Box, Button, Dialog, DialogContent, InputAdornment, TextField } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { alertConfirmDelete } from "components/Alert"
import { addIdForDataGrid, numberFormat, selectOnFocus } from "utils"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import dayjs from "dayjs"
import { GridActionsCellItem } from "@mui/x-data-grid-pro"
import { DeleteForeverRounded } from "resource/muiImport"
import DateTimeSelect from "components/DateTimeSelect"

const initFormData = {
  FuelDteSt: dayjs().format("YYYY-MM-DD 00:00:00"),
  FuelDteEn: dayjs().format("YYYY-MM-DD 23:59:59"),
  UntPr: "",
}

const FuelPriceCustomerDialog = ({ cusId, jobId, cusName, jobName, dialogOpen, setDialogOpen }) => {
  const { ax } = useContext(GlobalStateContext)
  const [dataTable, setDataTable] = useState([])
  const [filterMonth, setFilterMonth] = useState(dayjs().startOf("month"))
  const [formData, setFormData] = useState({ ...initFormData })

  const loadData = useCallback((month) => {
    if (!cusId) return
    const st = dayjs(month).startOf("month").format("YYYY-MM-DD")
    const en = dayjs(month).endOf("month").format("YYYY-MM-DD")
    ax.post("/cus-job/getFuelPriceCustomer", { CusId: cusId, JobId: jobId, FuelDteSt: st, FuelDteEn: en }).then(value => {
      if (value.data) {
        setDataTable(addIdForDataGrid(value.data, "FuelPrCusId"))
      }
    })
  }, [ax, cusId, jobId])

  useEffect(() => {
    if (dialogOpen) {
      setFilterMonth(dayjs().startOf("month"))
      setFormData({ ...initFormData })
      loadData(dayjs().startOf("month"))
    } else {
      setDataTable([])
    }
  }, [dialogOpen, loadData])

  const handleMonthChange = useCallback((newVal) => {
    setFilterMonth(newVal)
    if (newVal && newVal.isValid()) {
      loadData(newVal)
    }
  }, [loadData])

  const setSelectDate = useCallback((start, end) => {
    setFormData(o => ({ ...o, FuelDteSt: start, FuelDteEn: end }))
  }, [])

  const handleAdd = useCallback(async () => {
    if (!formData.FuelDteSt || !formData.FuelDteEn || formData.UntPr === "") return
    const value = await ax.post("/cus-job/insertFuelPriceCustomer", {
      CusId: cusId,
      JobId: jobId,
      FuelDteSt: formData.FuelDteSt,
      FuelDteEn: formData.FuelDteEn,
      UntPr: formData.UntPr,
    })
    if (value.data !== undefined) {
      loadData(filterMonth)
      setFormData(o => ({ ...o, UntPr: "" }))
    }
  }, [ax, cusId, formData, filterMonth, loadData, jobId])

  const handleDelete = useCallback((row) => {
    alertConfirmDelete(async () => {
      const value = await ax.post("/cus-job/deleteFuelPriceCustomer", { FuelPrCusId: row.FuelPrCusId })
      if (value.data) {
        setDataTable(o => o.filter(r => r.id !== row.id).map((r, i) => ({ ...r, No: i + 1 })))
      }
    })
  }, [ax])

  const columns = useMemo(() => [
    { field: "No", headerName: "No.", width: 50, align: "center" },
    {
      field: "FuelDte",
      headerName: "วันที่",
      width: 120,
      valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      field: "UntPr",
      headerName: "ราคา/ลิตร",
      width: 120,
      type: "number",
      align: "right",
      valueFormatter: ({ value }) => value != null ? numberFormat(value) : "-",
    },
    {
      field: "actions",
      type: "actions",
      width: 40,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<DeleteForeverRounded color="action" />}
          label="Delete"
          onClick={() => handleDelete(params.row)}
        />
      ]
    },
  ], [handleDelete])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => { if (reason === "backdropClick") return; setDialogOpen(false) }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth="sm"
    >
      <DialogHeader
        handleDialogClose={() => setDialogOpen(false)}
        title={`ราคาน้ำมันกำหนดเอง ${jobName ? ` - ${jobName}` : cusName ? ` - ${cusName}` : ""}`}
        onDeleteClick={null}
      />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 2 }}>
          <BoxFR sx={{ gap: 1 }}>
            <DesktopDatePicker
              openTo="month"
              views={["year", "month"]}
              disableMaskedInput
              label="เดือน-ปี"
              inputFormat="MMMM YYYY"
              value={filterMonth}
              onChange={handleMonthChange}
              renderInput={(params) => <TextField size="small" {...params} sx={{ width: 180 }} />}
            />
          </BoxFR>
          <BoxFR sx={{ gap: 1 }}>
            <DateTimeSelect
              label="วันที่"
              start={formData.FuelDteSt}
              end={formData.FuelDteEn}
              setSelectDate={setSelectDate}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label="ราคา/ลิตร"
              type="number"
              value={formData.UntPr}
              onChange={(e) => setFormData(o => ({ ...o, UntPr: e.target.value }))}
              onFocus={selectOnFocus}
              sx={{ width: 130 }}
              inputProps={{ style: { textAlign: "right" } }}
              InputProps={{ endAdornment: <InputAdornment position="end">บาท</InputAdornment> }}
            />

            <Button variant="contained" onClick={handleAdd}
              disabled={!formData.FuelDteSt || !formData.FuelDteEn || formData.UntPr === ""}>
              เพิ่ม
            </Button>
          </BoxFR>
          <Box height={320}>
            <DataGridCellExpand
              hideFooter
              hideToolbar
              rows={dataTable}
              columns={columns}
            />
          </Box>
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="" cancelText="ปิด"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={null}
      />
    </Dialog>
  )
}

export default React.memo(FuelPriceCustomerDialog)
